import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button, Chip, IconButton, InputAdornment, LinearProgress,
    MenuItem, Select, TextField
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../../app/lang/langs";
import styles from "../VReference.module.css";
import { config } from "../../../app/utils/config";
import { checkPermission } from "../../../app/utils/helpers";
import VReferenceModal from "../VReferenceModal";
import {
    VREFERENCE_STATUSES, VREFERENCE_STATUS_COLORS, VREFERENCE_STATUS_FALLBACK_COLOR,
    deleteVReference, getAllVReferences, getVReferencePostes,
    searchVReferences, setQuery, setStatusFilter
} from "../vreferenceSlice";

const SEARCH_DEBOUNCE_MS = 300

/**
 * The `sx` for a status pill, in the same brand tokens the desktop grid uses.
 *
 * Deliberately a small local copy rather than an import from VReferenceView: that file
 * imports this one to hand off on a phone, so importing back would close the cycle.
 */
const statusChipSx = (status) => ({
    ...(VREFERENCE_STATUS_COLORS[status] || VREFERENCE_STATUS_FALLBACK_COLOR),
    '& .MuiChip-label': { fontFamily: 'roboto', fontSize: 12 }
})

/**
 * Mobile V-Reference directory.
 *
 * Cards instead of a DataGrid — a seven-column grid is unusable on a phone — but everything
 * else is shared with the desktop view: the same slice, the same debounced search, and the
 * same VReferenceModal rendered fullScreen. Duplicating the form here would guarantee the
 * two drift apart the first time a field is added.
 */
export default function MobileVReferenceView() {

    const dispatch = useDispatch()
    const debounceRef = useRef(null)

    const list = useSelector((state) => state.vreference.list)
    const canManageAll = useSelector((state) => state.vreference.canManageAll)
    const query = useSelector((state) => state.vreference.query)
    const statusFilter = useSelector((state) => state.vreference.statusFilter)
    const userId = useSelector((state) => state.login.userId)
    const lang = useSelector((state) => state.settings.lang)

    const [isSearching, setIsSearching] = useState(false)
    const [selectedContact, setSelectedContact] = useState(null)
    const [showModal, setShowModal] = useState(false)

    const canEdit = checkPermission(config.scopes.vreference, config.permissionTypes.edit)
    const canManageRow = (row) => canManageAll || canEdit || row.created_by == userId

    // HR-only: the referral status and HR review note are not for the person who made the
    // introduction, even on their own contact. Gated on the edit tier, not on ownership.
    const canSeeHrFields = canManageAll || canEdit

    const runSearch = useCallback(async (searchQuery, status) => {
        setIsSearching(true)
        if (searchQuery && searchQuery.trim()) {
            await dispatch(searchVReferences({ query: searchQuery, status: status || undefined }))
        } else {
            await dispatch(getAllVReferences({ status: status || undefined }))
        }
        setIsSearching(false)
    }, [dispatch])

    useEffect(() => {
        dispatch(getVReferencePostes())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => runSearch(query, statusFilter), SEARCH_DEBOUNCE_MS)
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
    }, [query, statusFilter, runSearch])

    const reload = () => runSearch(query, statusFilter)

    const onDelete = (row) => {
        confirmDialog({
            message: Localize("VREFERENCE_DELETE_CONFIRM", lang)
                .replace("%name%", `${row.first_name} ${row.last_name}`),
            header: Localize("VREFERENCE_DELETE_TITLE", lang),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: Localize("YES", lang),
            rejectLabel: Localize("NO", lang),
            accept: async () => {
                const response = await dispatch(deleteVReference({ id: row.id }))
                if (response.payload && response.payload.responseData
                    && response.payload.responseData.status === 1) {
                    toast.success(Localize("VREFERENCE_DELETED", lang))
                    reload()
                } else {
                    toast.error(Localize("VREFERENCE_ACTION_FAILED", lang))
                }
            }
        })
    }

    const openModal = (contact) => {
        setSelectedContact(contact)
        setShowModal(true)
    }

    return (
        <div className={`${styles.vreference} ${styles.mobileContainer}`}>

            <div className={styles.mobileHeader}>
                <span className={styles.mobileTitle}>
                    <Langs str="VREFERENCE_TITLE" />
                </span>
                <Button
                    className={`${styles.pillButton} ${styles.pillButtonSmall}`}
                    startIcon={<PersonAddAlt1Icon />}
                    onClick={() => openModal(null)}
                >
                    <Langs str="VREFERENCE_ADD" />
                </Button>
            </div>

            <div className={styles.mobileToolbar}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={Localize("VREFERENCE_SEARCH_PLACEHOLDER", lang)}
                    value={query}
                    onChange={(e) => dispatch(setQuery(e.target.value))}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />
                {canSeeHrFields ? (
                    <Select
                        size="small"
                        displayEmpty
                        fullWidth
                        value={statusFilter}
                        onChange={(e) => dispatch(setStatusFilter(e.target.value))}
                        MenuProps={{ classes: { paper: styles.vreference } }}
                    >
                        <MenuItem value=""><Langs str="VREFERENCE_STATUS_ALL" /></MenuItem>
                        {VREFERENCE_STATUSES.map(status => (
                            <MenuItem key={status} value={status}>
                                <Langs str={`VREFERENCE_STATUS_${status.toUpperCase()}`} />
                            </MenuItem>
                        ))}
                    </Select>
                ) : null}
            </div>

            {isSearching
                ? <LinearProgress className={styles.searchProgress} />
                : <div className={styles.searchProgressPlaceholder} />}

            <div className={styles.mobileList}>
                {list.map(contact => (
                    <div key={contact.id} className={styles.mobileCard}>
                        <div className={styles.mobileCardHeader}>
                            <div>
                                <div className={styles.mobileName}>
                                    {contact.first_name} {contact.last_name}
                                </div>
                                <div className={styles.mobilePoste}>
                                    {contact.poste_name || contact.poste_label || '-'}
                                    {contact.company ? ` · ${contact.company}` : ''}
                                </div>
                            </div>
                            {/* HR-only. A non-HR card simply has no status pill — the
                                backend does not send the field to them either. */}
                            {canSeeHrFields ? (
                                <Chip
                                    size="small"
                                    className={styles.statusChip}
                                    sx={statusChipSx(contact.status)}
                                    label={Localize(`VREFERENCE_STATUS_${(contact.status || 'new').toUpperCase()}`, lang)}
                                />
                            ) : null}
                        </div>

                        <div className={styles.mobileMeta}>
                            {contact.email ? <span>{contact.email}</span> : null}
                            {contact.phone ? <span>{contact.phone}</span> : null}
                            <span>
                                <Langs str="VREFERENCE_REFERRED_BY" />: {contact.created_by_name || '-'}
                            </span>
                        </div>

                        {canManageRow(contact) ? (
                            <div className={styles.mobileActions}>
                                <IconButton size="small" onClick={() => openModal(contact)}>
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => onDelete(contact)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </div>
                        ) : null}
                    </div>
                ))}

                {!list.length ? (
                    <div className={styles.mobileEmpty}>
                        <Langs str="VREFERENCE_EMPTY" />
                    </div>
                ) : null}
            </div>

            <VReferenceModal
                show={showModal}
                fullScreen
                canSeeHrFields={canSeeHrFields}
                contact={selectedContact}
                onHide={() => { setShowModal(false); setSelectedContact(null) }}
                onSaved={() => { setShowModal(false); setSelectedContact(null); reload() }}
            />
        </div>
    )
}
